/**
 * @fileoverview Authentication Service - Centralized service for all authentication operations
 *
 * This service provides a secure, centralized interface for user authentication operations
 * including sign up, sign in, sign out, session management, and password reset functionality.
 * All authentication is handled through Supabase with additional security layers including
 * rate limiting, secure session management, and comprehensive logging.
 *
 * @module services/AuthService
 * @requires @/integrations/supabase/client
 * @requires @supabase/supabase-js
 * @requires @/lib/secure-session
 */

import { logAuditEvent, supabase, type Json } from '@xanectech/supabase-client';
import { User, Session } from '@supabase/supabase-js';
import { secureSession } from '../lib/secure-session';

import { createLogger } from '@xanectech/lib';
import { toast } from '@xanectech/ui/lib';
const logger = createLogger('Auth - AuthService');

/**
 * Result object returned from authentication operations
 *
 * @interface AuthResult
 * @property {User | null} user - The authenticated user object, or null if authentication failed
 * @property {Session | null} session - The session object containing tokens, or null if authentication failed
 * @property {Error | null} error - Error object if the operation failed, null on success
 */
export interface AuthResult {
  user: User | null;
  session: Session | null;
  error: Error | null;
}

export interface LoginAttemptLogInput {
  userId: string;
  email: string;
  portal: string;
  loginSuccessful: boolean;
}

export interface PortalAccessDeniedLogInput {
  userId: string;
  email: string;
  portal: string;
  currentRole?: string | null;
  denialReason: string;
  requestedPath?: string;
}

export interface LogoutAuditDetails extends Record<string, unknown> {
  email?: string | null;
  logout_reason?: string;
  occurred_at?: string;
  portal?: string;
  requested_path?: string;
  user_agent?: string;
  user_id?: string | null;
}

/**
 * Data required for user registration
 *
 * @interface SignUpData
 * @property {string} email - User's email address (required)
 * @property {string} password - User's password (required, must meet security requirements)
 * @property {string} [first_name] - User's first name (optional)
 * @property {string} [last_name] - User's last name (optional)
 * @property {string} [company_id] - Associated company ID for company users (optional)
 * @property {string} [role] - User role, defaults to 'end_user' (optional)
 */
export interface SignUpData {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  company_id?: string;
  role?: string;
}

/**
 * Authentication Service
 *
 * Provides static methods for all authentication operations including user registration,
 * login, logout, session management, and password operations. All methods are designed
 * to fail gracefully and return standardized result objects.
 *
 * @class AuthService
 * @static
 *
 * @example
 * // Sign up a new user
 * const result = await AuthService.signUp({
 *   email: 'user@example.com',
 *   password: 'SecurePass123!',
 *   first_name: 'John',
 *   last_name: 'Doe'
 * });
 *
 * if (!result.error) {
 *   logger.info('User signed up:', result.user);
 * }
 */
export class AuthService {
  /**
   * Register a new user account
   *
   * Creates a new user account with email verification. The user will receive
   * a confirmation email and will be redirected to the application after verification.
   * User metadata (first_name, last_name, company_id, role) is stored in the user's
   * auth.users metadata field.
   *
   * @param {SignUpData} userData - User registration data
   * @returns {Promise<AuthResult>} Authentication result with user, session, and error
   *
   * @example
   * const result = await AuthService.signUp({
   *   email: 'newuser@example.com',
   *   password: 'SecurePassword123!',
   *   first_name: 'Jane',
   *   last_name: 'Smith',
   *   role: 'end_user'
   * });
   *
   * if (result.error) {
   *   logger.error('Sign up failed:', result.error.message);
   * } else {
   *   logger.info('Sign up successful, check email for verification');
   * }
   */

  static async signUp(userData: SignUpData): Promise<AuthResult> {
    try {
      const redirectUrl = `${window.location.origin}/`;

      const { data, error } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: userData.first_name,
            last_name: userData.last_name,
            company_id: userData.company_id || null,
            role: userData.role || 'end_user',
          },
        },
      });

      if (error) throw error;

      return {
        user: data.user,
        session: data.session,
        error: null,
      };
    } catch (error) {
      return {
        user: null,
        session: null,
        error: error as Error,
      };
    }
  }

  /**
   * Authenticate a user with email and password
   *
   * Validates user credentials and establishes an authenticated session.
   * Returns the user object and session on success. This method should be
   * rate-limited on the client side to prevent brute force attacks.
   *
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @returns {Promise<AuthResult>} Authentication result with user, session, and error
   *
   * @example
   * const result = await AuthService.signIn(
   *   'user@example.com',
   *   'myPassword123!'
   * );
   *
   * if (result.error) {
   *   logger.error('Login failed:', result.error.message);
   * } else {
   *   logger.info('Logged in as:', result.user.email);
   *   // Log the successful login
   *   await AuthService.logLogin(result.user.id, result.user.email);
   * }
   */
  static async signIn(email: string, password: string): Promise<AuthResult> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast({
          title: `Login failed: ${error.message}`,
          variant: 'error',
        });
        logger.error('Login error:', { email, error: error.message });
        throw error;
      }

      return {
        user: data.user,
        session: data.session,
        error: null,
      };
    } catch (error) {
      return {
        user: null,
        session: null,
        error: error as Error,
      };
    }
  }

  /**
   * Sign out the current user and clear all session data
   *
   * Performs a secure sign out by:
   * 1. Logging the logout security event
   * 2. Ending the secure session (clears sensitive data)
   * 3. Signing out from Supabase auth
   * 4. Force clearing sensitive localStorage keys
   *
   * This method is designed to be thorough in clearing all authentication
   * and sensitive data from the client, even if some steps fail.
   *
   * @returns {Promise<{error: Error | null}>} Object containing error if sign out failed
   *
   * @example
   * const result = await AuthService.signOut();
   *
   * if (result.error) {
   *   logger.error('Sign out encountered an error:', result.error);
   *   // Still safe to proceed - sensitive data has been cleared
   * } else {
   *   logger.info('Successfully signed out');
   * }
   *
   * // Redirect to login page
   * window.location.href = '/auth';
   */
  static async signOut(
    details?: LogoutAuditDetails,
  ): Promise<{ error: Error | null }> {
    try {
      const logoutDetails: LogoutAuditDetails = {
        logout_reason: 'user_initiated',
        occurred_at: new Date().toISOString(),
        requested_path: window.location.pathname,
        user_agent: navigator.userAgent,
        ...details,
      };

      // Log security event before signing out
      await secureSession.logSecurityEvent(
        'user_logout_requested',
        logoutDetails,
      );

      // Securely end session (includes clearing sensitive data)
      await secureSession.endSession();

      // Attempt to sign out from Supabase
      const { error } = await supabase.auth.signOut();

      // Don't throw error if session not found - just clear state
      if (error && !error.message.includes('Session not found')) {
        logger.error('Sign out error:', error);
        return { error };
      }

      // Clear portal login deduplication flag so next login is recorded
      try {
        sessionStorage.removeItem('portal_login_logged');
      } catch {
        // sessionStorage unavailable — no-op
      }

      // Force clear sensitive localStorage keys
      const sensitiveKeys = [
        'user_roles',
        'current_role',
        'field_data',
        'sensitive_data',
        'encryption_key',
        'auth_token',
      ];

      sensitiveKeys.forEach((key) => {
        localStorage.removeItem(key);
        // Also remove dynamic keys
        Object.keys(localStorage).forEach((storeKey) => {
          if (storeKey.includes(key)) {
            localStorage.removeItem(storeKey);
          }
        });
      });

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  /**
   * Retrieve the current authentication session
   *
   * Gets the current session from Supabase, which includes access and refresh tokens.
   * The session is automatically refreshed if the access token has expired but the
   * refresh token is still valid.
   *
   * @returns {Promise<{session: Session | null, error: Error | null}>} Current session or error
   *
   * @example
   * const { session, error } = await AuthService.getCurrentSession();
   *
   * if (error) {
   *   logger.error('Failed to get session:', error);
   * } else if (!session) {
   *   logger.info('No active session - user not logged in');
   * } else {
   *   logger.info('Session expires at:', new Date(session.expires_at * 1000));
   * }
   */
  static async getCurrentSession(): Promise<{
    session: Session | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase.auth.getSession();

      if (error) throw error;

      return {
        session: data.session,
        error: null,
      };
    } catch (error) {
      return {
        session: null,
        error: error as Error,
      };
    }
  }

  /**
   * Retrieve the current authenticated user
   *
   * Gets the current user object from Supabase. This method validates the
   * current session and returns the user profile including metadata.
   *
   * @returns {Promise<{user: User | null, error: Error | null}>} Current user or error
   *
   * @example
   * const { user, error } = await AuthService.getCurrentUser();
   *
   * if (error) {
   *   logger.error('Failed to get user:', error);
   * } else if (!user) {
   *   logger.info('No authenticated user');
   * } else {
   *   logger.info('Current user:', user.email);
   *   logger.info('User metadata:', user.user_metadata);
   * }
   */
  static async getCurrentUser(): Promise<{
    user: User | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase.auth.getUser();

      if (error) throw error;

      return {
        user: data.user,
        error: null,
      };
    } catch (error) {
      return {
        user: null,
        error: error as Error,
      };
    }
  }

  /**
   * Initiate password reset process for a user
   *
   * Sends a password reset email to the specified address. The email contains
   * a secure link that redirects to the application with a reset token.
   *
   * @param {string} email - Email address of the account to reset
   * @returns {Promise<{error: Error | null}>} Error if reset initiation failed
   *
   * @example
   * const result = await AuthService.resetPassword('user@example.com');
   *
   * if (result.error) {
   *   logger.error('Password reset failed:', result.error.message);
   * } else {
   *   logger.info('Password reset email sent - check your inbox');
   * }
   */
  static async resetPassword(email: string): Promise<{ error: Error | null }> {
    try {
      const redirectUrl = `${window.location.origin}/auth?reset=true`;

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  /**
   * Update the password for the currently authenticated user
   *
   * Changes the password for the logged-in user. The user must be authenticated
   * and the new password must meet security requirements (minimum length,
   * complexity, etc.).
   *
   * @param {string} newPassword - The new password (must meet security requirements)
   * @returns {Promise<{error: Error | null}>} Error if password update failed
   *
   * @example
   * const result = await AuthService.updatePassword('NewSecurePass123!');
   *
   * if (result.error) {
   *   logger.error('Password update failed:', result.error.message);
   * } else {
   *   logger.info('Password updated successfully');
   *   // Optionally log the user out to require re-login with new password
   * }
   */
  static async updatePassword(
    newPassword: string,
  ): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  }

  /**
   * Log a portal-specific login outcome.
   *
   * Authentication success is not the same as successful portal access.
   * This method records the final outcome for a portal entry attempt so
   * login_history reflects whether the user was actually allowed into the app.
   */
  static async logLogin({
    userId,
    email,
    portal,
    loginSuccessful,
  }: LoginAttemptLogInput): Promise<void> {
    const basePayload = {
      p_user_id: userId,
      p_user_email: email,
      p_ip_address: undefined,
      p_user_agent: navigator.userAgent,
      p_login_method: 'password',
      p_portal: portal,
    };

    try {
      const { error } = await supabase.rpc('log_user_login', {
        ...basePayload,
        p_login_successful: loginSuccessful,
      });

      if (error) {
        const errorMessage = error.message ?? '';
        const likelyOldSignature =
          errorMessage.includes('p_login_successful') ||
          errorMessage.includes(
            'Could not find the function public.log_user_login',
          );

        if (!likelyOldSignature) {
          logger.error('Failed to log user login:', error);
          return;
        }

        logger.warn('Falling back to legacy log_user_login signature', {
          error: errorMessage,
        });

        if (!loginSuccessful) {
          logger.error(
            'Cannot record denied portal login with legacy DB signature. Apply migration for log_user_login(p_login_successful).',
            {
              portal,
              userId,
            },
          );
          return;
        }

        const { error: legacyError } = await supabase.rpc(
          'log_user_login',
          basePayload,
        );

        if (legacyError) {
          logger.error('Failed to log user login using legacy signature:', {
            error: legacyError.message,
            portal,
            userId,
          });
        }
      }
    } catch (error) {
      logger.error('Failed to log user login:', error as Error);
    }
  }

  static async logPortalAccessDenied({
    userId,
    email,
    portal,
    currentRole = null,
    denialReason,
    requestedPath,
  }: PortalAccessDeniedLogInput): Promise<void> {
    const details: Json = {
      email,
      current_role: currentRole,
      denial_reason: denialReason,
      login_method: 'password',
      occurred_at: new Date().toISOString(),
      portal,
      requested_path: requestedPath ?? window.location.pathname,
      user_agent: navigator.userAgent,
    };

    try {
      await logAuditEvent({
        action: 'portal_access_denied',
        details,
        resource_id: userId,
        resource_type: 'portal',
      });
    } catch (error) {
      logger.error('Failed to log portal access denial:', error as Error);
    }
  }

  /**
   * Check if an action is rate-limited for security
   *
   * Validates whether an action (e.g., login attempt) can proceed based on
   * rate limiting rules. This prevents brute force attacks and abuse.
   * The method fails closed (returns false) on errors for security.
   *
   * @param {string} identifier - Unique identifier for rate limiting (e.g., email, IP)
   * @param {number} [maxAttempts=5] - Maximum allowed attempts within the time window
   * @param {number} [windowMinutes=15] - Time window in minutes for rate limiting
   * @param {string} [actionType='login'] - Type of action being rate limited
   * @returns {Promise<boolean>} True if action is allowed, false if rate limited or on error
   *
   * @example
   * // Before allowing a login attempt
   * const isAllowed = await AuthService.checkRateLimit(
   *   email,
   *   5,    // max 5 attempts
   *   15,   // within 15 minutes
   *   'login'
   * );
   *
   * if (!isAllowed) {
   *   logger.error('Too many login attempts - please try again later');
   *   return;
   * }
   *
   * // Proceed with login
   * const result = await AuthService.signIn(email, password);
   */
  static async checkRateLimit(
    identifier: string,
    maxAttempts: number = 5,
    windowMinutes: number = 15,
    actionType: string = 'login',
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('enhanced_rate_limit_check', {
        p_identifier: identifier,
        p_max_attempts: maxAttempts,
        p_window_minutes: windowMinutes,
        p_action_type: actionType,
      });

      if (error) {
        logger.error('Error checking rate limit:', error);
        return false; // Fail closed for security
      }

      return data || false;
    } catch (err) {
      logger.error('Failed to check rate limit:', err as Error);
      return false; // Fail closed for security
    }
  }

  /**
   * Set up a listener for authentication state changes
   *
   * Registers a callback function that will be invoked whenever the authentication
   * state changes (user signs in, signs out, token refreshes, etc.).
   * Returns an unsubscribe function to stop listening.
   *
   * @param {Function} callback - Function called on auth state changes
   * @param {string} callback.event - Event type (e.g., 'SIGNED_IN', 'SIGNED_OUT', 'TOKEN_REFRESHED')
   * @param {Session | null} callback.session - Current session after the event
   * @returns {Object} Subscription object with an unsubscribe method
   *
   * @example
   * // Set up auth state listener
   * const { data: { subscription } } = AuthService.onAuthStateChange(
   *   (event, session) => {
   *     logger.info('Auth state changed:', event);
   *
   *     if (event === 'SIGNED_IN') {
   *       logger.info('User signed in:', session?.user.email);
   *     } else if (event === 'SIGNED_OUT') {
   *       logger.info('User signed out');
   *       // Redirect to login page
   *     } else if (event === 'TOKEN_REFRESHED') {
   *       logger.info('Token refreshed');
   *     }
   *   }
   * );
   *
   * // Later, to stop listening
   * subscription.unsubscribe();
   */
  static onAuthStateChange(
    callback: (event: string, session: Session | null) => void,
  ) {
    return supabase.auth.onAuthStateChange(callback);
  }
}
